/**
 * FmResponsive — canonical device-tier contract for Lehti AI DS.
 *
 * Device tiers from navigation-shell-responsiveness.md §8:
 *   desktop  ≥ 900px  — full shell, side-by-side layouts
 *   tablet   768–899px — panel→bottom-sheet, graphic stacks below
 *   mobile   < 768px  — minimum chrome, everything stacks
 *
 * Breakpoints match:
 *   - Sidebar.css ~900px seam (desktop↔tablet)
 *   - FmSplit stacks below the desktop tier (64.0625em, tokens.css)
 *
 * Pure props-in / value-out contract. NO Tailwind. NO shadcn. NO hardcoded hex.
 */
/* eslint-disable react-refresh/only-export-components --
 * DS utility module: intentionally co-exports the device-tier contract
 * (getDeviceTier, useFmResponsive, FM_BREAKPOINTS, types) alongside the
 * FmResponsive render-prop component. Splitting these apart would fragment the
 * single canonical responsive contract; Fast Refresh of this leaf module is a
 * non-concern. Mirrors the same-file export pattern used by the app contexts. */
import React from 'react';

export type FmDeviceTier = 'desktop' | 'tablet' | 'mobile';

/**
 * Tier boundaries (inclusive lower edge of the NEXT-larger tier):
 *   width ≥ tablet (900) → desktop
 *   width ≥ mobile (768) → tablet
 *   width <  mobile (768) → mobile
 */
export const FM_BREAKPOINTS = { tablet: 900, mobile: 768 } as const;

/**
 * Pure tier resolver — no hooks, no window access. Safe in SSR / tests.
 *   1280 → desktop, 900 → desktop, 899 → tablet, 768 → tablet, 767 → mobile
 */
export function getDeviceTier(width: number): FmDeviceTier {
  if (width >= FM_BREAKPOINTS.tablet) {
    return 'desktop';
  }
  if (width >= FM_BREAKPOINTS.mobile) {
    return 'tablet';
  }
  return 'mobile';
}

/**
 * Returns true when the DS contract requires the graphic/explanation
 * to stack below (single-column) — i.e. tablet or mobile tiers.
 * Consumed by FmSplit and FmFigure so the check is in ONE place, not per-component.
 */
export function shouldStackForTier(tier: FmDeviceTier): boolean {
  return tier === 'tablet' || tier === 'mobile';
}

/**
 * Live device tier hook. Tracks window.innerWidth via a resize listener.
 *
 * @param overrideWidth — when provided, the hook is fully deterministic and
 *   ignores window entirely (use for SSR snapshots and tests). When omitted,
 *   it reads window.innerWidth on mount and on every resize.
 */
export function useFmResponsive(overrideWidth?: number): FmDeviceTier {
  const hasWindow = typeof window !== 'undefined';

  const readWidth = React.useCallback((): number => {
    if (overrideWidth !== undefined) {
      return overrideWidth;
    }
    if (hasWindow) {
      return window.innerWidth;
    }
    // No window and no override — caller must supply a width in SSR.
    throw new Error(
      'useFmResponsive: no window available and no overrideWidth provided. ' +
        'Pass overrideWidth in SSR/non-DOM environments.',
    );
  }, [overrideWidth, hasWindow]);

  const [tier, setTier] = React.useState<FmDeviceTier>(() =>
    getDeviceTier(readWidth()),
  );

  React.useEffect(() => {
    if (overrideWidth !== undefined || !hasWindow) {
      // Deterministic / SSR: keep tier in sync with the (possibly changed) override.
      setTier(getDeviceTier(readWidth()));
      return;
    }
    const onResize = () => setTier(getDeviceTier(window.innerWidth));
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [overrideWidth, hasWindow, readWidth]);

  return tier;
}

export interface FmResponsiveProps {
  /**
   * Fixed tier (skips window measurement). When omitted the component measures
   * the live viewport via useFmResponsive.
   */
  tier?: FmDeviceTier;
  /** Render-prop child receiving the resolved device tier. */
  children: (tier: FmDeviceTier) => React.ReactNode;
}

/**
 * Render-prop wrapper exposing the resolved device tier to descendants.
 * Pure presentation: it computes a tier and hands it to `children`. No DOM
 * fabrication, no domain defaults.
 */
export const FmResponsive: React.FC<FmResponsiveProps> = ({ tier, children }) => {
  const measured = useFmResponsive(
    tier !== undefined ? tierToWidth(tier) : undefined,
  );
  const resolved = tier ?? measured;
  return <>{children(resolved)}</>;
};

/** Representative width for a fixed tier (so FmResponsive can short-circuit measurement). */
function tierToWidth(tier: FmDeviceTier): number {
  switch (tier) {
    case 'desktop':
      return FM_BREAKPOINTS.tablet;
    case 'tablet':
      return FM_BREAKPOINTS.mobile;
    case 'mobile':
      return FM_BREAKPOINTS.mobile - 1;
  }
}

export default FmResponsive;
